import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '../context/UserContext';
import { CSVLink } from 'react-csv';
import axios from "axios";
import './Enquiry.css';
import Modal from "react-modal";
import { Pie } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { hasPrivilege } from '../utils/hasPrivilege';
import Swal from 'sweetalert2';
import Select from 'react-select';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { buildDiffPreview, fireAudit } from '../utils/auditClient';

ChartJS.register(ArcElement, Tooltip, Legend);

const Enquiries = () => {
  const { userData } = useUser();
  const [statusFilter, setStatusFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [createdAtFilter, setCreatedAtFilter] = useState('');
  const [academies, setAcademies] = useState([]);
  const [selectedAcademy, setSelectedAcademy] = useState(null);
  const [academyFilter, setAcademyFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const today = new Date();
  const tenDaysAgo = new Date(today);
  const token = localStorage.getItem('auth-token');
  const { selectedCenter } = useUser();

  tenDaysAgo.setDate(today.getDate() - 10);
  const [startDate, setStartDate] = useState(tenDaysAgo.toISOString().split('T')[0]);

  today.setDate(today.getDate() + 1);
  const [endDate, setEndDate] = useState(today.toISOString().split('T')[0]);

  const updateBaselineRef = useRef({});

  const [saving, setSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (selectedCenter) {
      setAcademyFilter(selectedCenter);
    } else if (userData?.userType === 'ACADEMY' && userData.academyId) {
      setAcademyFilter("allAcademies");
    } else {
      setAcademyFilter(null);
    }
  }, [userData, selectedCenter]);

  const fetchEnquiries = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(process.env.REACT_APP_API_VIEW, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      const data = await response.json();

      if (data.success) {
        const apiData = data.data.apiData;
        const academiesResp = data.data.academies || [];

        const transformedData = apiData.map(item => {
          const matchedAcademy = academiesResp.find(a => a.id === item.academy_id);
          const academyName = item.academy_name || matchedAcademy?.name || '-';

          return {
            id: item.id || '-',
            name: item.name || '-',
            number: item.mobile_number || '-',
            academy: academyName,
            enquiry: item.remarks,
            source: item.source || 'Unknown',
            status: item.status || 'LEAD',
            createdAt: item.created_at,
            date_at: item.created_at,
            email: item.email,
            area: item.area,
            academyId: item.academy_id,
            query_message: item.query_message,
            external: item.is_external || false,
          };
        });

        setEnquiries(transformedData);
      } else {
        setError('Failed to fetch data');
      }
    } catch (error) {
      setError('Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, []);

  const headers = [
    { label: 'Name', key: 'name' },
    { label: 'Email', key: 'email' },
    { label: 'Number', key: 'number' },
    { label: 'Area', key: 'area' },
    { label: 'Centre', key: 'academy' },
    { label: 'Remark', key: 'enquiry' },
    { label: 'Date', key: 'date_at' },
    { label: 'Program', key: 'source' },
    { label: 'Status', key: 'status' },
    { label: 'Query', key: 'query_message' },
  ];

  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleItemsPerPageChange = (event) => {
    setRowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const uniqueAcademies = [...new Set(enquiries.map((enquiry) => enquiry.academy))];
  const uniqueSources = [...new Set(enquiries.map((enquiry) => enquiry.source))];
  const uniqueAreas = [...new Set(enquiries.map((enquiry) => enquiry.area))];
  const uniqueStatuses = [...new Set(enquiries.map((enquiry) => enquiry.status))];

  useEffect(() => {
    setCurrentPage(1);
  }, [academyFilter, sourceFilter, areaFilter, startDate, endDate, searchTerm]);

  const filteredEnquiries = enquiries.filter((enquiry) => {
    const createdAtDate = new Date(enquiry.createdAt);
    const adjustedEndDate = endDate ? new Date(new Date(endDate).setHours(23, 59, 59, 999)) : null;

    const isWithinDateRange =
      (!startDate || createdAtDate >= new Date(startDate)) &&
      (!adjustedEndDate || createdAtDate <= adjustedEndDate);

    const matchesAcademy = academyFilter === "allAcademies"
      ? userData.academyIds.includes(Number(enquiry.academyId))
      : academyFilter
        ? Number(enquiry.academyId) === Number(academyFilter)
        : true;

    return (
      Object.values(enquiry)
        .join(' ')
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) &&
      matchesAcademy &&
      (sourceFilter ? String(enquiry.source).toLowerCase() === String(sourceFilter).toLowerCase() : true) &&
      (areaFilter ? String(enquiry.area).toLowerCase() === String(areaFilter).toLowerCase() : true) &&
      (statusFilter ? String(enquiry.status).toLowerCase() === String(statusFilter).toLowerCase() : true) &&
      isWithinDateRange
    );
  });

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    const { key, direction } = sortConfig;
    if (!key) return data;

    const sorted = [...data].sort((a, b) => {
      if (key === 'date_at') {
        const aDate = new Date(a[key]);
        const bDate = new Date(b[key]);
        return direction === 'ascending' ? aDate - bDate : bDate - aDate;
      }

      const aValue = (a[key] ?? '').toString().toLowerCase();
      const bValue = (b[key] ?? '').toString().toLowerCase();

      if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
      return 0;
    });

    return sorted;
  };

  const sortedData = sortData(filteredEnquiries);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [resolvedStatus, setResolvedStatus] = useState('LEAD');

  const handleOpenModal = (enquiry) => {
    const matchedAcademy = academies.find((academy) => academy.name === enquiry.academy);
    setSelectedEnquiry(enquiry);
    setRemarks(enquiry.enquiry || "");
    setResolvedStatus(enquiry.status || "LEAD");
    const academyIdToUse = matchedAcademy ? matchedAcademy.id : academies[0]?.id;
    setSelectedAcademy(academyIdToUse);

    updateBaselineRef.current = {
      academyId: String(academyIdToUse ?? ''),
      status: enquiry.status || 'LEAD',
      remarks: enquiry.enquiry || '',
    };

    setModalIsOpen(true);
  };

  const handleCloseModal = () => {
    setModalIsOpen(false);
    setRemarks('');
  };

  const handleSaveStatus = async () => {
    if (!selectedEnquiry) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No enquiry selected!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    if (!selectedEnquiry.id) return;

    if (!remarks || remarks.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Remarks are required!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const selectedAcademyId = String(selectedAcademy);
    const matchedAcademy = academies.find((academy) => String(academy.id) === selectedAcademyId);

    const newPayload = {
      academyId: selectedAcademyId,
      status: resolvedStatus,
      remarks: remarks,
    };

    const { digest, html } = buildDiffPreview(updateBaselineRef.current || {}, newPayload);
    if (!digest.length) {
      Swal.fire({
        icon: 'info',
        title: 'Nothing to update',
        text: 'No differences detected.',
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2500,
      });
      return;
    }
    const confirm = await Swal.fire({
      title: 'Apply these changes?',
      html,
      showCancelButton: true,
      confirmButtonText: 'Update',
    });
    if (!confirm.isConfirmed) return;

    const baseUrl =
      selectedEnquiry.external === true
        ? `${process.env.REACT_APP_API_UPDATE}/${selectedEnquiry.id}`
        : `${process.env.REACT_APP_API_UPDATE_INTERNAL}/${selectedEnquiry.id}`;

    try {
      setSaving(true);

      const response = await fetch(baseUrl, {
        method: "POST",
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(newPayload),
      });

      fireAudit({
        entity: 'enquiry',
        affected_id: selectedEnquiry.id,
        action: 'update',
        section: 'MARKETING',
        subSection: 'Enquiry',
        businessApi: baseUrl,
        businessMethod: 'POST',
        oldPayload: updateBaselineRef.current || {},
        newPayload,
        extra: { page: 'Enquiries', external: !!selectedEnquiry.external },
      });

      if (response.ok) {
        const updatedEnquiries = enquiries.map((enquiry) =>
          enquiry.id === selectedEnquiry.id
            ? {
              ...enquiry,
              academy: matchedAcademy ? matchedAcademy.name : enquiry.academy,
              status: resolvedStatus,
              enquiry: remarks,
            }
            : enquiry
        );

        setEnquiries(updatedEnquiries);

        updateBaselineRef.current = { ...newPayload };

        handleCloseModal();
        Swal.fire({
          icon: "success",
          title: "Success",
          text: `Successfully updated! Academy: ${matchedAcademy ? matchedAcademy.name : "Unknown"}`,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        const errorText = await response.text();
        Swal.fire({
          icon: "error",
          title: "Update Failed",
          text: `Error updating enquiry status: ${errorText}`,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Request Failed",
        text: "There was an error making the API request. Please try again.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const sources = [
    "Swimming-SummerCamp",
    "Badminton-SummerCamp",
    "Badminton-Coaching",
    "Swimming-playing",
    "Swimming-Coaching",
    "Swimming-Marketing",
    "Badminton-Marketing",
    "EXTERNAL"
  ];

  const pieChartData = (source) => {
    const statuses = ['LEAD', 'CALLED', 'CONVERTED', 'INTERESTED', 'JUNK'];
    const colors = ['#36A2EB', '#FFCE56', '#FF9F40', '#4BC0C0', '#9966FF', '#FF6384', '#C9CBCF', '#8BC34A'];

    let dataValues = statuses.map(
      (status) => filteredEnquiries.filter((enquiry) => enquiry.source === source && enquiry.status === status).length
    );

    const filteredData = dataValues
      .map((value, index) => (value > 0 ? { value, label: statuses[index], color: colors[index] } : null))
      .filter(Boolean);

    if (filteredData.length === 0) return null;

    return {
      labels: filteredData.map((item) => item.label),
      datasets: [
        {
          data: filteredData.map((item) => item.value),
          backgroundColor: filteredData.map((item) => item.color),
          hoverBackgroundColor: filteredData.map((item) => item.color),
        },
      ],
    };
  };

  const [file, setFile] = useState(null);
  const [serverResponse, setServerResponse] = useState('');
  const fileInputRef = useRef(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);
  const [fileName, setFileName] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile || null);
    setFileName(selectedFile ? selectedFile.name : '');
  };

  const handleClose = () => {
    setShowModal(false);
    setFile(null);
    setFileName("");
    setServerResponse("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const uploadCSV = async () => {
    if (!file) {
      setServerResponse("Please select a CSV file.");
      return;
    }

    let formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      const res = await axios.post(process.env.REACT_APP_API_CSV, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        }
      });
      setServerResponse(res.data);

      fireAudit({
        entity: 'enquiry_csv_upload',
        affected_id: null,
        action: 'create',
        section: 'MARKETING',
        subSection: 'Import',
        businessApi: process.env.REACT_APP_API_CSV,
        businessMethod: 'POST',
        oldPayload: {},
        newPayload: { fileName },
        extra: { page: 'Enquiries', size: file.size, type: file.type },
      });
      handleClose();

      fetchEnquiries();
      Swal.fire({
        icon: "success",
        title: "Upload Complete",
        text: "CSV processed successfully!",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } catch (error) {
      setServerResponse("Error: " + (error?.response?.data || error.message));

      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "There was an error processing the CSV.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleUploadButtonClick = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setShowModal(false);
    }
  };

  useEffect(() => {
    if (showModal) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showModal]);

  const handleSendMessage = async (enquiry) => {
    const apiUrl = process.env.REACT_APP_API_SEND_MESSAGE;

    const headers = {
      'Content-Type': 'application/json',
      'x-api-key': process.env.REACT_APP_API_KEY,
    };

    const payload = {
      name: enquiry.name,
      academy: enquiry.academy,
      area: enquiry.area,
      number: `+91${enquiry.number}`,
      source: enquiry.source,
      status: enquiry.status,
    };

    try {
      setSendingId(enquiry.id);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload)
      });

      const responseData = await response.json();

      fireAudit({
        entity: 'enquiry_message',
        affected_id: enquiry.id,
        action: 'create',
        section: 'MARKETING',
        subSection: 'Broadcast',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: {},
        newPayload: payload,
        extra: { page: 'Enquiries', result: responseData?.message },
      });

      if (responseData.message === 'Message sent successfully') {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Message sent successfully!",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: `Error: ${responseData.message}`,
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      fireAudit({
        entity: 'enquiry_message',
        affected_id: enquiry.id,
        action: 'create',
        section: 'MARKETING',
        subSection: 'Broadcast',
        businessApi: apiUrl,
        businessMethod: 'POST',
        oldPayload: {},
        newPayload: payload,
        extra: { page: 'Enquiries', error: error?.message || 'send_failed' },
      });

      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: `Error sending message: ${error.message}`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setSendingId(null);
    }
  };

  useEffect(() => {
    const fetchAcademies = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_QUERY_VIEW, {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        const filteredAcademies = response.data.data.academies.map((academy) => ({
          id: academy.id,
          name: academy.name,
        }));

        setAcademies(filteredAcademies);

        if (filteredAcademies.length > 0) {
          setSelectedAcademy(filteredAcademies[0].id);
        }
      } catch (error) {
      }
    };

    fetchAcademies();
  }, []);

  const totalEntries = filteredEnquiries.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;

  return (
    <div className="enrollment-container">
      <div className="sales-filters-login" style={{ gap: '15px' }}>

        <div className="floating-select">
          <Select
            value={academies.find(opt => String(opt.id) === String(academyFilter)) || null}
            className={`sales-filter-court-booking ${academyFilter ? "has-value" : ""}`}
            onChange={(selectedOption) => setAcademyFilter(selectedOption ? selectedOption.id : '')}
            options={
              userData.userType === "ACADEMY"
                ? [{ id: 'allAcademies', name: 'All Academies' }, ...academies.filter(a => userData.academyIds.includes(a.id))]
                : [{ id: '', name: 'All Academies' }, ...academies]
            }
            placeholder='Academy'
            getOptionLabel={(e) => e.name}
            getOptionValue={(e) => e.id}
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
            menuPortalTarget={document.body}
          />
          {academyFilter && <label htmlFor="academy">Academy</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${sourceFilter ? "has-value" : ""}`}
            value={sourceFilter ? { label: sourceFilter, value: sourceFilter } : null}
            onChange={(selectedOption) => setSourceFilter(selectedOption ? selectedOption.value : '')}
            options={[{ label: 'All Sources', value: '' }, ...uniqueSources.map(source => ({ label: source, value: source }))]}
            placeholder='All Sources'
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
            menuPortalTarget={document.body}
          />
          {sourceFilter && <label htmlFor="source">Source</label>}
        </div>

        <div className="floating-select">
          < Select
            className={`sales-filter-court-booking ${statusFilter ? "has-value" : ""}`}
            value={statusFilter ? { label: statusFilter, value: statusFilter } : null}
            onChange={(selectedOption) => setStatusFilter(selectedOption ? selectedOption.value : '')}
            options={[{ label: 'All Statuses', value: '' }, ...uniqueStatuses.map(status => ({ label: status, value: status }))]}
            placeholder='Status'
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
            menuPortalTarget={document.body}
          />
          {statusFilter && <label htmlFor="status">Status</label>}
        </div>

        <div className="floating-select">
          <Select
            className={`sales-filter-court-booking ${areaFilter ? "has-value" : ""}`}
            value={areaFilter ? { label: areaFilter, value: areaFilter } : null}
            onChange={(selectedOption) => setAreaFilter(selectedOption ? selectedOption.value : '')}
            options={[{ label: 'All Areas', value: '' }, ...uniqueAreas.map(area => ({ label: area, value: area }))]}
            placeholder='Area'
            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
            }}
            menuPortalTarget={document.body}
          />
          {areaFilter && <label htmlFor="area">Area</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            placeholder="Start Date"
            name="startDate"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            className={`sales-filter-court-booking ${startDate ? "has-value" : ""}`}
          />
          {startDate && <label htmlFor="startDate">Start Date</label>}
        </div>

        <div className="floating-select">
          <input
            type="date"
            placeholder="End Date"
            name="endDate"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            onClick={(e) => e.target.showPicker()}
            max={new Date().toISOString().split('T')[0]}
            className={`sales-filter-court-booking ${endDate ? "has-value" : ""}`}
          />
          {endDate && <label htmlFor="endDate">End Date</label>}
        </div>

        <div className="floating-select" style={{ maxWidth: '60px' }}>
          <button className="tt-action-btn" onClick={fetchEnquiries} disabled={loading}>
            <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="unique-charts-query">
        {sources.map((source, index) => {
          const chartData = pieChartData(source);
          if (!chartData) return null;
          return (
            <div key={index} className="unique-chart-query">
              <h3 style={{ textAlign: 'center' }}>{source}</h3>
              <Pie data={chartData} />
            </div>
          );
        })}
      </div>

      <div className="search-create-container">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? 'has-value' : ''}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>

        <CSVLink
          data={filteredEnquiries}
          headers={headers}
          filename="Enquiry.csv"
          className="btn btn-primary no-decoration"
        >
          <button className="csv_button" type="button">
            <span className="button__text">Export CSV</span>
            <span className="button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 35 35" className="svg">
                <path d="M17.5,22.131a1.249,1.249,0,0,1-1.25-1.25V2.187a1.25,1.25,0,0,1,2.5,0V20.881A1.25,1.25,0,0,1,17.5,22.131Z"></path>
                <path d="M17.5,22.693a3.189,3.189,0,0,1-2.262-.936L8.487,15.006a1.249,1.249,0,0,1,1.767-1.767l6.751,6.751a.7.7,0,0,0,.99,0l6.751-6.751a1.25,1.25,0,0,1,1.768,1.767l-6.752,6.751A3.191,3.191,0,0,1,17.5,22.693Z"></path>
                <path d="M31.436,34.063H3.564A3.318,3.318,0,0,1,.25,30.749V22.011a1.25,1.25,0,0,1,2.5,0v8.738a.815.815,0,0,0,.814.814H31.436a.815.815,0,0,0,.814-.814V22.011a1.25,1.25,0,1,1,2.5,0v8.738A3.318,3.318,0,0,1,31.436,34.063Z"></path>
              </svg>
            </span>
          </button>
        </CSVLink>
      </div>

      <div className="items-container">
        <select
          className="items-per-page-select"
          value={rowsPerPage}
          onChange={handleItemsPerPageChange}
        >
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>

        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() => handleItemsPerPageChange({ target: { value: option } })}
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {hasPrivilege(userData, 'MARKETING', 'EQUIRY_MODIFY') && (
          <div style={{ marginTop: '20px', textAlign: 'right' }}>
            <button
              onClick={() => setShowModal(true)}
              className="create-button"
              style={{ width: window.innerWidth <= 600 ? "100px" : "100%" }}
            >
              Upload CSV
            </button>

            <br />
            <a
              href="https://machaxidev.blob.core.windows.net/frontend-machaxi/sample_query.xlsx"
              download
              className="download-csv-link"
            >
              Download Sample CSV
            </a>

            {showModal && (
              <div style={styles.modal}>
                <div ref={modalRef} style={styles.modalContent}>
                  <h2>Upload CSV File</h2>
                  <input
                    id="enquiry-file"
                    type="file"
                    onChange={handleFileChange}
                    accept=".csv"
                    ref={fileInputRef}
                    style={{ display: "none" }}
                  />
                  <div className="enquiry-container">
                    <div className="enquiry-header" onClick={handleUploadButtonClick}>
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 10V9C7 6.23858 9.23858 4 12 4C14.7614 4 17 6.23858 17 9V10C19.2091 10 21 11.7909 21 14C21 15.4806 20.1956 16.8084 19 17.5M7 10C4.79086 10 3 11.7909 3 14C3 15.4806 3.8044 16.8084 5 17.5M7 10C7.43285 10 7.84965 10.0688 8.24006 10.1959M12 12V21M12 12L15 15M12 12L9 15"
                          stroke="#000" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        </path>
                      </svg>
                      <p>Choose File</p>
                    </div>
                    {fileName && <p style={{ marginTop: "10px", fontSize: "14px" }}>📂 {fileName}</p>}

                    <button
                      onClick={uploadCSV}
                      className="enquiry-footer"
                      disabled={uploading}
                    >
                      {uploading ? "Uploading…" : "Upload"}
                    </button>
                  </div>

                  <p id="response" style={styles.response}>{serverResponse}</p>

                  <button onClick={handleClose} style={styles.closeButton}>
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span><span>O</span><span>A</span><span>D</span><span>I</span><span>N</span><span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredEnquiries.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''} ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'MARKETING', 'EQUIRY_MODIFY') && (<th>Action</th>)}
            </tr>
          </thead>
          <tbody>
            {currentRows.map((enquiry) => (
              <tr key={`${enquiry.id}-${enquiry.createdAt}`}>
                <td>{enquiry.name}</td>
                <td>{enquiry.email || '-'}</td>
                <td>{enquiry.number}</td>
                <td>{enquiry.area}</td>
                <td>{enquiry.academy}</td>
                <td>{enquiry.enquiry || '-'}</td>
                <td>{new Date(enquiry.date_at).toISOString().slice(0, 16).replace("T", " ")}</td>
                <td>{enquiry.source}</td>
                <td>{enquiry.status}</td>
                <td>{enquiry.query_message}</td>

                {hasPrivilege(userData, 'MARKETING', 'EQUIRY_MODIFY') && (
                  <td>
                    <button className="resolve-button" onClick={() => handleOpenModal(enquiry)}>
                      {enquiry.enquiry ? 'Update Remark' : 'Add Remark'}
                    </button>
                    <button
                      className="resolved-button"
                      onClick={() => handleSendMessage(enquiry)}
                      disabled={sendingId === enquiry.id}
                    >
                      {sendingId === enquiry.id ? 'Sending…' : 'Send Message'}
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={handleCloseModal}
        contentLabel="Update Enquiry Status"
        ariaHideApp={false}
        className="update-status-modal"
        overlayClassName="update-status-overlay"
      >
        <h2 className="modal-title">Update Status for {selectedEnquiry?.name}</h2>

        <div>
          <textarea
            placeholder="Add remarks"
            value={remarks}
            onChange={(e) => setRemarks(e.target.value)}
            className="remarks-textarea"
            required
          />
        </div>

        <div className="status-radio-group" style={{ display: 'block' }}>
          <h4 className='status-query'> Select Status:</h4>
          {["LEAD", "CALLED", "CONVERTED", "INTERESTED", "JUNK"].map((status) => (
            <label key={status}>
              <input
                type="radio"
                value={status}
                checked={resolvedStatus === status}
                onChange={(e) => setResolvedStatus(e.target.value)}
              />
              {status}
            </label>
          ))}
        </div>

        <div className="academy-dropdown">
          <label>
            <span className='status-query'>Select Academy:</span>
            <select
              value={selectedAcademy ?? ''}
              onChange={(e) => setSelectedAcademy(e.target.value)}
              className="academy-select"
            >
              {academies.map((academy) => (
                <option key={academy.id} value={academy.id}>
                  {academy.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="modal-actions">
          <button onClick={handleSaveStatus} className="save-button-marketing" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button onClick={handleCloseModal} className="cancel-button-marketing" disabled={saving}>
            Cancel
          </button>
        </div>
      </Modal>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

const styles = {
  container: { display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '20px' },
  button: { padding: '10px 20px', margin: '10px', cursor: 'pointer' },
  response: { marginTop: '20px', fontSize: '14px', color: 'green' },
  fileName: { marginTop: '10px', fontSize: '16px', fontWeight: 'bold' },
  modal: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
  },
  modalContent: { backgroundColor: 'white', padding: '30px', borderRadius: '10px', textAlign: 'center', width: '400px' },
  closeButton: { backgroundColor: 'red', color: 'white', border: 'none', padding: '10px 20px', cursor: 'pointer' },
};

export default Enquiries;
